/**
 * Inbox message body — email HTML (Gmail/Outlook-like) + plain chat fallback.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Download, FileText, Image as ImageIcon, ImageOff, Paperclip } from "lucide-react";

const BUBBLE_TEXT_CLASS = cn(
  "whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word]",
  "max-w-full min-w-0",
);

/** HTML bodies must NOT use pre-wrap — newlines after <br> / between <p> tags
 *  would render as extra blank lines (e.g. huge gaps in "Best regards," sign-offs). */
const BUBBLE_HTML_CLASS = cn(
  "whitespace-normal break-words [overflow-wrap:anywhere] [word-break:break-word]",
  "max-w-full min-w-0",
);

const EMAIL_HTML_ALLOWED_TAGS = [
  "a", "b", "i", "em", "strong", "u", "s", "strike", "sub", "sup",
  "p", "br", "ul", "ol", "li", "div", "span", "font", "center",
  "table", "thead", "tbody", "tfoot", "tr", "th", "td", "colgroup", "col",
  "img", "blockquote", "hr", "pre", "code",
  "h1", "h2", "h3", "h4", "h5", "h6",
];

const EMAIL_HTML_ALLOWED_ATTR = [
  "href", "target", "rel", "src", "alt", "title", "class", "style",
  "width", "height", "colspan", "rowspan", "align", "valign", "border",
  "cellpadding", "cellspacing", "bgcolor", "color", "face", "size", "name",
  "loading", "decoding",
];

const EMAIL_IFRAME_CSS = `
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
  }
  body {
    padding: 12px 14px;
    font-family: "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #202124;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  img {
    max-width: 100% !important;
    height: auto !important;
    border: 0;
    background: #f1f5f9;
  }
  table {
    border-collapse: collapse;
    max-width: 100%;
  }
  td, th {
    vertical-align: top;
  }
  a {
    color: #1a73e8;
    text-decoration: underline;
    cursor: pointer;
    pointer-events: auto;
  }
  /* Avoid stacked quote bars on nested lists (composer Tab/Quote wrappers). */
  blockquote {
    margin: 0;
    padding-left: 0;
    border: none !important;
    border-left: none !important;
    color: inherit;
  }
  p {
    margin: 0 0 0.5em 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul, ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
    border: none !important;
    list-style-position: outside;
  }
  li {
    margin: 0.15em 0;
    border: none !important;
    color: inherit;
  }
  pre {
    white-space: pre-wrap;
    font-family: monospace;
    font-size: 13px;
  }
`;

import { stripHtml } from "@/lib/emailComposer";

export function normalizeCid(cid) {
  const raw = String(cid || "").trim();
  const stripped = raw.replace(/(^<)|(>$)/g, "");
  const withoutScheme = stripped.toLowerCase().startsWith("cid:")
    ? stripped.slice(4)
    : stripped;
  return withoutScheme.split("@")[0].toLowerCase();
}

function preprocessEmailHtml(html) {
  return String(html || "")
    .replace(/<!--\[if[\s\S]*?endif\]-->/gi, "")
    .replace(/<o:p>\s*<\/o:p>/gi, "");
}

function _sanitizeAnchorNode(node) {
  node.setAttribute("target", "_blank");
  node.setAttribute("rel", "noopener noreferrer");
}

function _sanitizeImageNode(node) {
  if (!node.getAttribute("loading")) node.setAttribute("loading", "lazy");
  if (!node.getAttribute("decoding")) node.setAttribute("decoding", "async");
  const src = node.getAttribute("src") || "";
  if (src.startsWith("/api/")) {
    node.setAttribute("src", resolveAttachmentUrl(src));
  }
}

function _sanitizeStyleAttributes(node) {
  const style = node.getAttribute("style");
  if (!style || !/border(?:-(?:left|right|top|bottom))?\s*:/i.test(style)) {
    return;
  }
  const cleanedStyle = style
    .replace(/border(?:-(?:left|right|top|bottom))?\s*:[^;]*(?:;|$)/gi, "")
    .replace(/;;+/g, ";")
    .trim()
    .replace(/(?:^;)|(?:;$)/g, "");
  if (cleanedStyle) node.setAttribute("style", cleanedStyle);
  else node.removeAttribute("style");
}

function _handleAfterSanitizeAttributes(node) {
  if (node.tagName === "A") {
    _sanitizeAnchorNode(node);
    return;
  }
  if (node.tagName === "IMG") {
    _sanitizeImageNode(node);
    return;
  }
  if (["BLOCKQUOTE", "UL", "OL", "LI", "DIV", "P"].includes(node.tagName)) {
    _sanitizeStyleAttributes(node);
  }
}

export function sanitizeEmailHtml(html) {
  if (!html || typeof html !== "string") return "";
  const cleaned = resolveEmailMediaUrls(preprocessEmailHtml(html));
  if (typeof window === "undefined") {
    return stripHtml(cleaned);
  }
  DOMPurify.addHook("afterSanitizeAttributes", _handleAfterSanitizeAttributes);
  const safe = DOMPurify.sanitize(cleaned, {
    ALLOWED_TAGS: EMAIL_HTML_ALLOWED_TAGS,
    ALLOWED_ATTR: EMAIL_HTML_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
  DOMPurify.removeHook("afterSanitizeAttributes");
  return safe;
}

/** Absolute URL for same-origin / proxied attachment paths. */
export function resolveAttachmentUrl(url) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:") || url.startsWith("https:") || url.startsWith("http:") || url.startsWith("blob:")) {
    return url;
  }
  if (url.startsWith("/api/")) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${url}`;
  }
  return url;
}

function resolveEmailMediaUrls(html) {
  if (!html || typeof html !== "string") return html || "";
  return html.replace(
    /src=(["'])(\/api\/conversations\/[^"']+)\1/gi,
    (_, quote, path) => `src=${quote}${resolveAttachmentUrl(path)}${quote}`,
  );
}

/** True for Gmail compose attachments (data-uri/cid images), not https marketing HTML. */
export function hasEmbeddedAttachmentImages(metadata, html = "") {
  const source = html || metadata?.body_html || "";
  if (/src=["']data:image/i.test(source)) return true;
  if (/\/api\/conversations\/[^"']+\/attachments\//i.test(source)) return true;
  return (metadata?.attachments || []).some((a) => {
    const url = String(a?.url || "");
    return a?.type === "image" && (
      url.startsWith("data:image")
      || url.includes("/attachments/")
    );
  });
}

function buildEmailIframeDocument(bodyHtml, { hideScrollbar = false } = {}) {
  const bodyOverflow = hideScrollbar ? "overflow: hidden;" : "";
  const css = `${EMAIL_IFRAME_CSS}\n  body { ${bodyOverflow} }`;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const base = origin ? `<base href="${origin}/">` : "";
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">${base}<style>${css}</style></head><body>${bodyHtml}</body></html>`;
}

function looksLikeHtml(text) {
  return /<[a-z][\s\S]*>/i.test(text || "");
}

function normalizeBodyText(text, metadata) {
  const raw = (text || "").replace(/\u00a0/g, " ").trim();
  const subject = (metadata?.subject || "").trim();
  const html = (metadata?.body_html || "").trim();
  if (html) {
    const plain = stripHtml(html).replace(/\u00a0/g, " ").trim();
    if (plain) return plain;
  }
  if (raw && !(subject && raw === subject)) {
    if (raw.includes("<") && raw.includes(">") && looksLikeHtml(raw)) {
      return stripHtml(raw) || raw;
    }
    return raw;
  }
  if (subject) return subject;
  return "(No message content)";
}

const RICH_HTML_TAG_RE = /<(img|table|video|iframe|object|style|center|blockquote)\b/i;

/** Semantic / list / emphasis tags that must render as HTML, not stripped plain text. */
const FORMATTED_HTML_TAG_RE =
  /<(?:b|i|em|strong|u|s|strike|sub|sup|ul|ol|li|a|h[1-6]|pre|code|hr)\b/i;

/** Gmail/Outlook often wrap plain compose text in a single <div> or <p> — not a rich email. */
export function isPlainTextOnlyHtml(html) {
  const cleaned = preprocessEmailHtml(html || "").trim();
  if (!cleaned) return true;
  if (RICH_HTML_TAG_RE.test(cleaned)) return false;
  if (FORMATTED_HTML_TAG_RE.test(cleaned)) return false;
  // Inline styles often carry bold, color, or indentation from the original email.
  if (/\sstyle\s*=/i.test(cleaned)) return false;
  const withoutSimpleTags = cleaned.replace(
    /<\/?(?:div|p|br|span|font)\b[^>]*>/gi,
    "",
  );
  return !/<[a-z]/i.test(withoutSimpleTags);
}

export function emailHtmlSource(text, metadata) {
  const html = (metadata?.body_html || "").trim();
  if (html) return html;
  const raw = (text || "").trim();
  if (looksLikeHtml(raw)) return raw;
  // Markdown → HTML is done on the backend (metadata.body_html). Do not
  // re-parse Markdown in the browser.
  return "";
}

/** True when the message has real HTML layout or image attachments (not plain text only). */
export function isRichEmailMessage(text, metadata) {
  if ((metadata?.attachments || []).some((a) => a?.type === "image" && a?.url)) return true;
  const html = emailHtmlSource(text, metadata);
  if (!html) return false;
  return !isPlainTextOnlyHtml(html);
}

/** Plain text visible in an email body (HTML stripped), ignoring subject fallback. */
export function emailPlainTextContent(text, metadata) {
  const html = emailHtmlSource(text, metadata);
  if (html) {
    const fromHtml = stripHtml(html).replace(/\u00a0/g, " ").trim();
    if (fromHtml) return fromHtml;
  }
  return String(text || "").replace(/\u00a0/g, " ").trim();
}

/**
 * True when the email has no real body — only a subject (or nothing).
 * Image attachments still count as content.
 */
export function isSubjectOnlyEmailMessage(text, metadata) {
  if (emailGalleryAttachments(metadata).length > 0) return false;
  if (hasEmbeddedAttachmentImages(metadata)) return false;
  const subject = String(metadata?.subject || "").trim();
  const plain = emailPlainTextContent(text, metadata);
  if (!plain) return true;
  return Boolean(subject) && plain === subject;
}

/** True when an image is pasted/referenced inside the HTML body (not a file attach). */
export function isAttachmentEmbeddedInHtml(attachment, html) {
  if (!html || typeof html !== "string") return false;
  const lower = html.toLowerCase();
  const cid = normalizeCid(attachment?.content_id || attachment?.cid);
  if (cid && lower.includes(`cid:${cid}`)) return true;
  const url = String(attachment?.url || "").trim();
  if (!url) return false;
  if (lower.includes(url.toLowerCase())) return true;
  const resolved = resolveAttachmentUrl(url);
  if (resolved && resolved !== url && lower.includes(String(resolved).toLowerCase())) return true;
  const pathMatch = url.match(/(\/api\/conversations\/[^"'?\s]+\/attachments\/\d+)/i);
  return Boolean(pathMatch && lower.includes(pathMatch[1].toLowerCase()));
}

/**
 * File attachments for the Gmail-style card grid.
 * Pasted/inline images stay in the email body and are excluded.
 */
export function emailGalleryAttachments(metadata) {
  const html = metadata?.body_html || "";
  const gallery = metadata?.gallery_attachments;
  if (Array.isArray(gallery)) {
    // Trust backend list (including empty). Drop any still embedded in the body.
    return gallery.filter((a) => a?.url && !isAttachmentEmbeddedInHtml(a, html));
  }
  // Legacy messages without gallery_attachments: only explicit file attachments.
  return (metadata?.attachments || []).filter((a) => {
    if (a?.type !== "image" || !a?.url) return false;
    if (a?.disposition === "inline") return false;
    if (isAttachmentEmbeddedInHtml(a, html)) return false;
    return a?.disposition === "attachment" || !a?.disposition;
  });
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Remove <img> tags that duplicate gallery file attachments so the body
 * does not show the same image full-size above the Gmail-style cards.
 */
export function stripGalleryImagesFromHtml(html, galleryAttachments) {
  if (!html || typeof html !== "string") return html || "";
  const urls = (galleryAttachments || [])
    .map((a) => String(a?.url || "").trim())
    .filter(Boolean);
  if (urls.length === 0) return html;

  let result = html;
  for (const url of urls) {
    const variants = new Set([url, resolveAttachmentUrl(url)]);
    for (const variant of variants) {
      if (!variant) continue;
      const re = new RegExp(
        `<img\\b[^>]*\\bsrc=["']${escapeRegExp(variant)}["'][^>]*/?>`,
        "gi",
      );
      result = result.replace(re, "");
    }
    // Proxy path without host: /api/conversations/.../attachments/N
    const pathMatch = url.match(/(\/api\/conversations\/[^"'?\s]+\/attachments\/\d+)/i);
    if (pathMatch) {
      const pathRe = new RegExp(
        `<img\\b[^>]*\\bsrc=["'][^"']*${escapeRegExp(pathMatch[1])}[^"']*["'][^>]*/?>`,
        "gi",
      );
      result = result.replace(pathRe, "");
    }
  }
  return result.replace(/(?:<br\s*\/?>\s*){2,}/gi, "<br>").trim();
}

function attachmentFileMeta(attachment) {
  const filename = String(attachment?.filename || "Attachment").trim() || "Attachment";
  const mime = String(attachment?.mime || "").toLowerCase();
  const ext = (filename.includes(".") ? filename.split(".").pop() : "").toLowerCase();
  const isImage = attachment?.type === "image"
    || mime.startsWith("image/")
    || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext);

  if (isImage) {
    return {
      filename,
      isImage: true,
      accent: "#34a853",
      badgeClass: "bg-emerald-600 text-white",
      Icon: ImageIcon,
      typeLabel: "IMG",
    };
  }
  if (mime.includes("pdf") || ext === "pdf") {
    return {
      filename,
      isImage: false,
      accent: "#ea4335",
      badgeClass: "bg-red-600 text-white",
      Icon: FileText,
      typeLabel: "PDF",
    };
  }
  if (
    mime.includes("word")
    || mime.includes("officedocument.wordprocessing")
    || ["doc", "docx"].includes(ext)
  ) {
    return {
      filename,
      isImage: false,
      accent: "#4285f4",
      badgeClass: "bg-blue-600 text-white",
      Icon: FileText,
      typeLabel: "W",
    };
  }
  return {
    filename,
    isImage: false,
    accent: "#5f6368",
    badgeClass: "bg-slate-600 text-white",
    Icon: FileText,
    typeLabel: (ext || "FILE").slice(0, 4).toUpperCase(),
  };
}

function isSafeExternalUrl(href) {
  if (!href || typeof href !== "string") return false;
  const trimmed = href.trim();
  if (trimmed.startsWith("#")) return false;
  try {
    const url = new URL(trimmed, window.location.origin);
    return url.protocol === "https:" || url.protocol === "mailto:";
  } catch {
    return false;
  }
}

function EmailHtmlFrame({ html, hideScrollbar = false }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(1);
  const srcDoc = useMemo(
    () => buildEmailIframeDocument(html, { hideScrollbar }),
    [html, hideScrollbar],
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const next = Math.max(
          doc.body?.scrollHeight || 0,
          doc.documentElement?.scrollHeight || 0,
          1,
        );
        setHeight(Math.min(next + 2, 6000));
      } catch {
        setHeight(120);
      }
    };

    const watchImages = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        doc.querySelectorAll("img").forEach((img) => {
          if (img.complete) return;
          img.addEventListener("load", resize, { once: true });
          img.addEventListener("error", resize, { once: true });
        });
      } catch {
        // ignore
      }
    };

    const onLinkClick = (event) => {
      const anchor = event.target?.closest?.("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!isSafeExternalUrl(href)) return;
      event.preventDefault();
      if (href.trim().toLowerCase().startsWith("mailto:")) {
        window.location.href = href;
        return;
      }
      window.open(href, "_blank", "noopener,noreferrer");
    };

    const attachLinkHandler = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) return;
        doc.body.addEventListener("click", onLinkClick);
      } catch {
        // ignore
      }
    };

    const onLoad = () => {
      resize();
      watchImages();
      attachLinkHandler();
    };

    iframe.addEventListener("load", onLoad);
    const timer = window.setTimeout(() => {
      resize();
      watchImages();
      attachLinkHandler();
    }, 60);
    const timer2 = window.setTimeout(() => {
      resize();
      watchImages();
    }, 300);
    const timer3 = hideScrollbar ? window.setTimeout(() => {
      resize();
      watchImages();
    }, 1200) : undefined;
    return () => {
      iframe.removeEventListener("load", onLoad);
      try {
        iframe.contentDocument?.body?.removeEventListener("click", onLinkClick);
      } catch {
        // ignore
      }
      window.clearTimeout(timer);
      window.clearTimeout(timer2);
      if (timer3) window.clearTimeout(timer3);
    };
  }, [srcDoc, hideScrollbar]);

  return (
    <iframe
      ref={iframeRef}
      title="Email message"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      scrolling={hideScrollbar ? "no" : "auto"}
      className={cn(
        "w-full border-0 bg-white block",
        hideScrollbar && "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      )}
      style={{ height, overflow: hideScrollbar ? "hidden" : undefined }}
      data-testid="email-html-frame"
    />
  );
}

EmailHtmlFrame.propTypes = {
  html: PropTypes.string.isRequired,
  hideScrollbar: PropTypes.bool,
};

function MessageAttachmentCard({ attachment, onPreview }) {
  const [broken, setBroken] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);
  const url = resolveAttachmentUrl(attachment?.url);
  const meta = attachmentFileMeta(attachment);
  const { filename, isImage, accent, badgeClass, Icon, typeLabel } = meta;

  useEffect(() => {
    setBroken(false);
    setLoaded(false);
  }, [url]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el || !url || broken || !isImage) return undefined;
    if (el.complete && el.naturalWidth > 0) {
      setLoaded(true);
    }
    const handleError = () => setBroken(true);
    const handleLoad = () => setLoaded(true);
    el.addEventListener("error", handleError);
    el.addEventListener("load", handleLoad);
    return () => {
      el.removeEventListener("error", handleError);
      el.removeEventListener("load", handleLoad);
    };
  }, [url, broken, isImage]);

  const openPreview = () => {
    if (isImage && url && !broken) onPreview(attachment);
  };

  const downloadHref = url || "#";

  return (
    <div
      className="group relative w-[168px] shrink-0 overflow-hidden rounded border border-slate-300 bg-white shadow-sm hover:shadow-md transition-shadow"
      data-testid="attachment-card"
    >
      <button
        type="button"
        className="block w-full text-left"
        onClick={openPreview}
        aria-label={filename}
        disabled={!isImage || !url || broken}
      >
        <div className="relative h-[112px] bg-[#f8f9fa] flex items-center justify-center overflow-hidden">
          {isImage && url && !broken ? (
            <>
              {!loaded && (
                <div
                  className="absolute inset-0 animate-pulse bg-slate-100"
                  data-testid="attachment-image-placeholder"
                  aria-hidden
                />
              )}
              <img
                ref={imgRef}
                src={url}
                alt={filename}
                className={cn(
                  "h-full w-full object-cover relative",
                  loaded ? "opacity-100" : "opacity-0",
                )}
                loading="lazy"
                decoding="async"
              />
            </>
          ) : (
            <div className="flex flex-col items-center gap-1.5 text-slate-400">
              {broken ? <ImageOff size={28} /> : <Icon size={32} strokeWidth={1.5} />}
              <span className="text-[10px] font-medium uppercase tracking-wide">{typeLabel}</span>
            </div>
          )}
        </div>
      </button>

      <a
        href={downloadHref}
        download={filename}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "absolute right-1.5 top-1.5 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full",
          "bg-white/95 text-slate-700 shadow ring-1 ring-slate-200",
          "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto",
          "focus-visible:opacity-100 focus-visible:pointer-events-auto transition-opacity",
        )}
        aria-label={`Download ${filename}`}
        title="Download"
        onClick={(e) => e.stopPropagation()}
      >
        <Download size={14} />
      </a>

      <div className="relative flex items-center gap-1.5 border-t border-slate-200 bg-[#f1f3f4] px-2 py-1.5 pr-4">
        <span
          className={cn(
            "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded px-1 text-[9px] font-bold leading-none",
            badgeClass,
          )}
          aria-hidden
        >
          {typeLabel}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12px] text-[#202124]" title={filename}>
          {filename}
        </span>
        <span
          className="pointer-events-none absolute bottom-0 right-0 h-0 w-0"
          style={{
            borderBottom: `10px solid ${accent}`,
            borderLeft: "10px solid transparent",
          }}
          aria-hidden
        />
      </div>
    </div>
  );
}

MessageAttachmentCard.propTypes = {
  attachment: PropTypes.shape({
    url: PropTypes.string,
    filename: PropTypes.string,
    type: PropTypes.string,
    mime: PropTypes.string,
    content_id: PropTypes.string,
    cid: PropTypes.string,
    disposition: PropTypes.string,
  }),
  onPreview: PropTypes.func.isRequired,
};

export function MessageAttachments({ attachments, className }) {
  const items = (attachments || []).filter((a) => a?.url);
  const [preview, setPreview] = useState(null);
  if (items.length === 0) return null;

  const countLabel = items.length === 1 ? "1 Attachment" : `${items.length} Attachments`;

  return (
    <>
      <div
        className={cn("max-w-full min-w-0 border-t border-slate-200 pt-3", className)}
        data-testid="message-attachments"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="text-[12px] text-slate-600 inline-flex items-center gap-1.5">
            <Paperclip size={12} className="text-slate-400" />
            <span className="font-medium text-slate-700">{countLabel}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {items.map((att, index) => (
            <MessageAttachmentCard
              key={`${att.url}-${att.filename || index}`}
              attachment={att}
              onPreview={setPreview}
            />
          ))}
        </div>
      </div>
      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-3xl p-2" data-testid="image-preview-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>{preview?.filename || "Image preview"}</DialogTitle>
          </DialogHeader>
          {preview?.url && (
            <img
              src={resolveAttachmentUrl(preview.url)}
              alt={preview.filename || "Preview"}
              className="max-h-[80vh] w-full object-contain rounded-md"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

MessageAttachments.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      filename: PropTypes.string,
      type: PropTypes.string,
      mime: PropTypes.string,
      content_id: PropTypes.string,
      cid: PropTypes.string,
      disposition: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
};

/** Gmail/Outlook-style HTML body for received email messages. */
export function EmailMessageBody({ text, metadata, className }) {
  const gallery = useMemo(() => emailGalleryAttachments(metadata), [metadata]);
  const htmlSource = useMemo(() => {
    const raw = emailHtmlSource(text, metadata);
    return stripGalleryImagesFromHtml(raw, gallery);
  }, [text, metadata, gallery]);
  const plainWrapper = htmlSource && isPlainTextOnlyHtml(htmlSource);
  const safeHtml = useMemo(
    () => (htmlSource && !plainWrapper ? sanitizeEmailHtml(htmlSource) : ""),
    [htmlSource, plainWrapper],
  );
  const hideAttachmentScrollbar = hasEmbeddedAttachmentImages(metadata, safeHtml || htmlSource);
  // Image-only emails have no textContent after stripHtml — still render the iframe.
  const hasVisibleHtml = Boolean(
    safeHtml
    && (
      stripHtml(safeHtml).replace(/\u00a0/g, " ").trim()
      || /<img\b/i.test(safeHtml)
    ),
  );
  const subject = String(metadata?.subject || "").trim();
  const plain = emailPlainTextContent(text, metadata);
  const subjectOnly = isSubjectOnlyEmailMessage(text, metadata);

  // Attachment-only mail: body was just the file preview — cards render below.
  if (!hasVisibleHtml && gallery.length > 0 && !plain && !subjectOnly) {
    return null;
  }

  if (hasVisibleHtml) {
    return (
      <div className={cn("min-w-0 bg-white", className)} data-testid="email-message-body">
        <EmailHtmlFrame html={safeHtml} hideScrollbar={hideAttachmentScrollbar} />
      </div>
    );
  }

  if (subjectOnly) {
    return (
      <div
        className={cn("px-4 py-3 text-[14px] text-slate-800", className)}
        data-testid="email-message-body-subject-only"
      >
        {subject ? (
          <>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Subject
            </div>
            <div className="mt-0.5 font-medium text-slate-900 [overflow-wrap:anywhere]">
              {subject}
            </div>
            <div className="mt-2 text-[12px] text-slate-500">(No message body)</div>
          </>
        ) : (
          <span className="text-slate-500">(No message content)</span>
        )}
      </div>
    );
  }

  // Empty body with attachments — avoid "(No message content)" above the cards.
  if (gallery.length > 0 && !plain) {
    return null;
  }

  const display = plain || normalizeBodyText(text, metadata);
  return (
    <div className={cn("px-4 py-3 text-[14px] text-slate-800 whitespace-pre-wrap break-words", className)} data-testid="email-message-body-plain">
      {display}
    </div>
  );
}

EmailMessageBody.propTypes = {
  text: PropTypes.string,
  metadata: PropTypes.shape({
    subject: PropTypes.string,
    body_html: PropTypes.string,
    attachments: PropTypes.array,
  }),
  className: PropTypes.string,
};

export function MessageBody({ text, metadata, className }) {
  const gallery = useMemo(() => emailGalleryAttachments(metadata), [metadata]);
  const htmlSource = useMemo(() => {
    const raw = emailHtmlSource(text, metadata);
    return stripGalleryImagesFromHtml(raw, gallery);
  }, [text, metadata, gallery]);
  const safeHtml = useMemo(
    () => (htmlSource ? sanitizeEmailHtml(htmlSource) : ""),
    [htmlSource],
  );
  const hasVisibleHtml = Boolean(
    safeHtml
    && (
      stripHtml(safeHtml).replace(/\u00a0/g, " ").trim()
      || /<img\b/i.test(safeHtml)
    ),
  );

  if (hasVisibleHtml) {
    return (
      <div
        className={cn(
          BUBBLE_HTML_CLASS,
          "email-html-body [&_a]:text-indigo-600 [&_a]:underline [&_table]:max-w-full [&_td]:align-top [&_img]:max-w-full [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_li]:my-0 [&_strong]:font-semibold [&_p]:m-0 [&_p]:mb-2 [&_p:last-child]:mb-0",
          className,
        )}
        data-testid="message-body-html"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }

  if (gallery.length > 0) {
    const plain = emailPlainTextContent(text, metadata);
    if (!plain) return null;
  }

  const display = normalizeBodyText(text, metadata);
  return (
    <div className={cn(BUBBLE_TEXT_CLASS, className)} data-testid="message-body">
      {display}
    </div>
  );
}

MessageBody.propTypes = {
  text: PropTypes.string,
  metadata: PropTypes.shape({
    subject: PropTypes.string,
    body_html: PropTypes.string,
    attachments: PropTypes.array,
  }),
  className: PropTypes.string,
};

export default MessageBody;
